import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from "react";
import { useIntl } from "react-intl";

// Components
import {
    Modal, ModalBody, ModalFooter, Button, Checkbox, ToggleSwitch
} from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';
import EntitySelector from './.components/entitySelector';

// Helpers
import { copyToClipboard } from 'Core/data/Helpers';

// Actions
import { openModalEntitySelector } from 'Redux/actions';
import { syncEntities, reset } from './redux/slice';

// Constants
const ENTITY_KEYS = [
    "account", "units", "subusers", "drivers", "assets", /* "asset_types", */ "asset_subtypes",
];

const SyncEntities = () => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        account, loading, openModal,
        result, error, type, entity
    } = useSelector(state => state.syncEntitiesRedux);

    const openEntitySelector = useSelector(state => state.syncEntitiesSelectorRedux.openModal);

    const [internalAccount, setInternalAccount] = useState(null);
    const [entities, setEntities] = useState(() =>
        ENTITY_KEYS.reduce((acc, key) => ({ ...acc, [key]: key === "account" }), {})
    );
    const [entitySwitch, setEntitySwitch] = useState(() =>
        ENTITY_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    const [units, setUnits] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [assets, setAssets] = useState([]);
    const [subusers, setSubusers] = useState([]);
    const [assetSubtypes, setAssetSubtypes] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);

    const entitySetters = useMemo(() => ({
        units: setUnits,
        drivers: setDrivers,
        assets: setAssets,
        subusers: setSubusers,
        asset_subtypes: setAssetSubtypes,
        asset_types: setAssetTypes,
    }), []);

    const entityData = useMemo(() => ({
        units,
        drivers,
        assets,
        subusers,
        asset_subtypes: assetSubtypes,
        asset_types: assetTypes,
    }), [units, drivers, assets, subusers, assetSubtypes, assetTypes]);

    const handleCheckboxChange = (key) => {
        setEntities(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleEntitySwitchChange = (key, value) => {
        setEntitySwitch(prev => ({ ...prev, [key]: value }));
        if (value) {
            if (key === 'units') {
                setUnits([])
            } else if (key === 'drivers') {
                setDrivers([])
            }
            else if (key === 'assets') {
                setAssets([])
            }
            else if (key === 'subusers') {
                setSubusers([])
            }
            else if (key === 'asset_subtypes') {
                setAssetSubtypes([])
            }
            else if (key === 'asset_types') {
                setAssetTypes([])
            }
        }

        if (!value) {
            const selected = entityData[key] || [];
            dispatch(openModalEntitySelector({
                entities: selected,
                type: key,
                accountId: account?.id
            }));
        }
    };

    const closeModal = () => {
        setInternalAccount(null);
        dispatch(reset());
    };

    const handleSave = () => {
        try {
            let newEntities = []
            const selectedEntities = Object.entries(entities)
                .filter(([_, value]) => value)
                .map(([key]) => key);

            selectedEntities.map((item) => {
                if (item !== 'account') {
                    let current = entityData[item]
                    let swichValue = entitySwitch[item];
                    newEntities.push({
                        entityName: item,
                        entityIds: (current?.length && !entitySwitch[item]) ? current?.map(entity => entity?.id) : swichValue ? [-99] : []
                    })
                } else {
                    newEntities.push({
                        entityName: "account",
                        entityIds: [account?.id]
                    })
                }
            })

            let unselectedEntities = null
            if (newEntities.length) {
                newEntities.forEach((item) => {
                    if (!item.entityIds || !item.entityIds.length) {
                        unselectedEntities = item?.entityName || false;
                    }
                })
            }
            if (unselectedEntities) {
                NotificationManager.error(
                    `${messages['sync_entities_selected_for']} ${messages[`sync_entities_${unselectedEntities}`] || unselectedEntities}`,
                    messages['sync_entities_title'],
                    3000
                );
            } else {
                dispatch(syncEntities({
                    companyId: account?.id,
                    entities: newEntities,
                }));
            }
        } catch (error) {
            NotificationManager.error(
                messages['sync_entities_error_messages'],
                messages['sync_entities_title'],
                3000
            );
        }
    };

    useEffect(() => {
        if (!type || !entity?.length) return;
        const setFn = entitySetters[type];
        if (setFn) setFn(entity);
    }, [type, entity, entitySetters]);

    useEffect(() => {
        if (openModal && account) {
            setInternalAccount({
                id: account?.id,
                name: account?.name,
                status: account?.status
            });
        }
    }, [openModal, account]);

    useEffect(() => {
        if (result) {
            const { transactionId } = result;
            const message = messages["sync_entities_success_messages"];
            NotificationManager.success(message, messages['sync_entities_title'], 10000, () => {
                copyToClipboard(transactionId, messages['common_copied']);
                navigate(`/app/transactions/list/${transactionId}`);
            });
            closeModal();
        }

        if (error) {
            NotificationManager.error(
                messages['sync_entities_error_messages'],
                messages['sync_entities_title'],
                3000
            );
            dispatch(reset({ error: null }));
        }
    }, [result, error]);

    return (
        <>
            <Modal
                open={openModal}
                title={`${messages['sync_entities_title']} ${account?.name ? `- ${account.name}` : ''}`}
                handleClose={closeModal}
                position="right"
                loading={loading}
                size="small"
            >
                <ModalBody>
                    <div style={{ padding: '0 10px' }}>
                        <div style={{ width: '100%', display: 'grid', marginBottom: 30 }}>
                            <label style={{ fontWeight: 'bold', marginBottom: 10 }}>
                                {messages['sync_entities_entities_to_sync']}
                                <span className="text-danger"> *</span>
                            </label>

                            {ENTITY_KEYS.map(key => (
                                <div key={key}>
                                    <Checkbox
                                        label={messages[`sync_entities_${key}`] || key}
                                        className="itemCheck mb-0"
                                        type="checkbox"
                                        id={`${key}-check`}
                                        value={entities[key]}
                                        onChange={() => handleCheckboxChange(key)}
                                    />
                                    {entities[key] && key !== "account" && (
                                        <div style={{ marginLeft: 30, display: 'flex', alignItems: 'center' }}>
                                            <ToggleSwitch
                                                id={`toggle-${key}`}
                                                large
                                                blueToggle
                                                optionLabels={[messages['sync_entities_all'], messages['sync_entities_selected']]}
                                                checked={entitySwitch[key]}
                                                onChange={(val) => handleEntitySwitchChange(key, val)}
                                            />
                                            {!entitySwitch[key] && (
                                                <span
                                                    style={{
                                                        fontSize: 15,
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                    }}
                                                    onClick={() => {
                                                        handleEntitySwitchChange(key, false)
                                                    }}
                                                >
                                                    {entityData[key]?.length || 0}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button className="btn btn-cancel" onClick={closeModal}>
                        {messages['common_back']}
                    </Button>
                    <Button
                        className="btn-dark-blue"
                        disabled={!(internalAccount?.id && Object.values(entities).some(v => v))}
                        onClick={handleSave}
                    >
                        {messages['sync_entities_process']}
                    </Button>
                </ModalFooter>
            </Modal>
            {openEntitySelector && <EntitySelector />}
        </>
    );
};

export default SyncEntities;